import { useCallback, useRef } from 'react';

import {
  IPaymentDetails,
  PaymentRequestError,
  RequestPaymentInput,
  RequestPaymentResult,
  paymentErrorType,
} from 'features/payment/types';
import { STORE_UNAVAILABLE } from 'features/store/constants';

import { IRequestApplePayPayment, IUseApplePay } from 'state/apple-pay/hooks/types';
import useApplePay from 'state/apple-pay/hooks/use-apple-pay';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';
import { logger } from 'utils/logger';
import { truncateServerOrderForLog } from 'utils/logger/utils';

type PromiseCallbacks = {
  resolve: (value: IPaymentDetails | undefined) => void;
  reject: (error: Error) => void;
};

export const useRequestApplePayPayment = ({
  applePay: applePayInput,
}: RequestPaymentInput): RequestPaymentResult => {
  const { billingCountry = ISOs.USA, tip, isForPrepaidReload, serverOrder, reloadAmount } =
    applePayInput ?? {};
  const { countryCode, currencyCode } = getCountryAndCurrencyCodes(billingCountry);
  const promiseRef = useRef<PromiseCallbacks | null>(null);
  // Tracks whether the PaymentAuthorization listener has already fired (with data or error).
  // useApplePay registers two native listeners:
  //   1. PaymentAuthorization  → fires with actual payment data or triggers onError
  //   2. PaymentAuthorizationClosed → always fires when the sheet closes, calls handleApplePay({})
  // The "closed" callback can arrive before or after the auth result. We use this flag to
  // distinguish a genuine user cancellation (closed WITHOUT auth) from a post-auth close.
  const authorizationResultReceivedRef = useRef(false);

  const handleApplePay: IUseApplePay['handleApplePay'] = async ({
    data: applePayDetails,
    error,
  }) => {
    if (error) {
      authorizationResultReceivedRef.current = true;
      promiseRef.current?.reject(new PaymentRequestError(paymentErrorType.applePayGeneric));
      promiseRef.current = null;
      return;
    }

    if (!applePayDetails) {
      // handleApplePay({}) (no data, no error) comes from the PaymentAuthorizationClosed
      // listener — it just means the sheet was dismissed. If auth already fired, the promise
      // is already settled. If it hasn't, we received an empty payment details object —
      // reject with genericError to show an error dialog (matches old behavior).
      // TODO: This should be removed once we have the Apple Pay SDK updated to handle this case.
      if (authorizationResultReceivedRef.current) {
        return;
      }
      logger.info({
        isPaymentLog: true,
        message: '[useRequestApplePayPayment][handleApplePay] Apple Pay sheet dismissed',
      });
      promiseRef.current?.reject(new PaymentRequestError(paymentErrorType.applePayCancelled));
      promiseRef.current = null;
      return;
    }

    authorizationResultReceivedRef.current = true;
    promiseRef.current?.resolve({ applePayDetails });
    promiseRef.current = null;
  };

  const { requestApplePayPayment, formatApplePayCartPayload } = useApplePay({
    handleApplePay,
    tipAmount: tip,
    prepaid: isForPrepaidReload,
    serverOrder,
  });

  const requestPayment = useCallback((): Promise<IPaymentDetails | undefined> => {
    // Cancel / clear any existing promise before creating a new one
    promiseRef.current?.reject(new PaymentRequestError(paymentErrorType.applePayCancelled));
    promiseRef.current = null;
    authorizationResultReceivedRef.current = false;

    return new Promise<IPaymentDetails | undefined>((resolve, reject) => {
      const applePayParams: IRequestApplePayPayment | null = isForPrepaidReload
        ? {
            countryCode,
            currencyCode,
            total: reloadAmount ?? 0,
          }
        : formatApplePayCartPayload();

      if (!applePayParams) {
        logger.error({
          isPaymentLog: true,
          message:
            '[useRequestApplePayPayment][requestPayment] Apple Pay params could not be generated.',
          isForPrepaidReload,
          reloadAmount,
          billingCountry,
          tip,
          serverOrder: truncateServerOrderForLog(serverOrder),
        });
        reject(new PaymentRequestError(paymentErrorType.applePayGeneric));
        return;
      }

      // wire up useApplePay to our existing promise created here
      // after requestApplePayPayment is called, the promise will be resolved or rejected
      promiseRef.current = { resolve, reject };

      try {
        requestApplePayPayment(applePayParams);
      } catch (error) {
        logger.error({
          isPaymentLog: true,
          message:
            '[useRequestApplePayPayment][requestPayment] Error occurred while requesting Apple Pay payment.',
          error,
        });

        const errorType =
          error instanceof Error && error.message === STORE_UNAVAILABLE
            ? paymentErrorType.storeUnavailable
            : paymentErrorType.applePayGeneric;

        promiseRef.current?.reject(new PaymentRequestError(errorType));
        promiseRef.current = null;
      }
    });
  }, [
    billingCountry,
    countryCode,
    currencyCode,
    formatApplePayCartPayload,
    isForPrepaidReload,
    reloadAmount,
    requestApplePayPayment,
    serverOrder,
    tip,
  ]);

  return { requestPayment };
};
